import React, { useState } from "react";

import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react";

import { BiSolidCheckCircle } from "react-icons/bi";
import { FaCloudUploadAlt } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";

import { toastError } from "@/util/helpers";

interface UploadCSVProps {
  isOpen: boolean;
  onClose: () => void;
  isUploading: boolean;
  onUpload: (file: File) => void;
}

export const UploadCSV: React.FC<UploadCSVProps> = ({
  isOpen,
  onClose,
  isUploading,
  onUpload
}) => {
  const [file, setFile] = useState<{ id: string; file: File } | null>(null);
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    // Check file size
    if (uploadedFile.size > MAX_FILE_SIZE) {
      toastError(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`);
      return;
    }

    // Check file type
    if (!uploadedFile.name.toLowerCase().endsWith(".csv")) {
      toastError("Only CSV files are allowed");
      return;
    }

    setFile({
      id: uuidv4(),
      file: uploadedFile
    });
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (file) {
      onUpload(file.file);
    }
  };

  const getFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1048576).toFixed(2)} MB`;
  };

  return (
    <Modal
      size="2xl"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent py={8} px={8}>
        <ModalCloseButton />
        <Box w="full">
          <Heading
            as="h2"
            fontSize="xl"
            mb={2}
            color="secondary.500"
            borderBottom="1px solid #E0E0E0"
            pb={2}
          >
            Add multiple users
          </Heading>
          <Text color="gray.600" fontSize="sm" mb={6}>
            To add a group of users who can forward invoices to Jack, download
            the{" "}
            <Text
              as="a"
              href="/templates/email_forwarding_senders.csv"
              download
              color="secondary.500"
              fontWeight="semibold"
              textDecoration="underline"
            >
              CSV file
            </Text>
            . Populate the file with the relevant user information and upload it
            back into the system.
          </Text>

          <Stack
            as="form"
            onSubmit={handleSubmit}
            opacity={isUploading ? 0.5 : 1}
            pointerEvents={isUploading ? "none" : "initial"}
            spacing={6}
            w="full"
          >
            <Stack spacing={4}>
              {!file && (
                <Center
                  pos="relative"
                  flexShrink={0}
                  cursor="pointer"
                  h={250}
                  flexDir="column"
                  justifyContent="space-between"
                  border="1px dashed"
                  borderColor="gray.400"
                  w="full"
                  rounded={8}
                  py={6}
                >
                  <Input
                    as="input"
                    zIndex={5}
                    opacity={0}
                    w="full"
                    h="full"
                    pos="absolute"
                    top={0}
                    cursor="pointer"
                    type="file"
                    onChange={handleUploadFile}
                    accept=".csv"
                  />

                  <Box />
                  <VStack>
                    <Icon
                      as={FaCloudUploadAlt}
                      fontSize={70}
                      color="primary.500"
                    />
                    <VStack spacing={0}>
                      <Text
                        fontSize="md"
                        color="gray.600"
                        fontWeight="semibold"
                      >
                        Drop files here or
                      </Text>
                      <Text
                        fontSize="md"
                        color="blue.500"
                        fontWeight="semibold"
                      >
                        Browse
                      </Text>
                    </VStack>
                  </VStack>

                  <VStack spacing={1}>
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      Format: csv
                    </Text>
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      1 file upload limit at a time
                    </Text>
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      Max Total file size 15MB
                    </Text>
                  </VStack>
                </Center>
              )}

              {file && (
                <HStack w="full" spacing={4}>
                  <Stack
                    spacing={1}
                    w="full"
                    shadow="md"
                    rounded={10}
                    border="1px solid"
                    borderColor="gray.300"
                    px={5}
                    py={2}
                  >
                    <HStack justify="space-between">
                      <Text color="black" fontWeight={500} fontSize={12}>
                        {file.file.name}
                      </Text>
                      <Icon color="green.500" as={BiSolidCheckCircle} />
                    </HStack>
                    <Box bg="green.400" w="full" rounded="full" h={1.5} />
                    <HStack
                      fontSize="xs"
                      color="gray.500"
                      justify="space-between"
                    >
                      <Text>{getFileSize(file.file.size)}</Text>
                      <Text>Ready</Text>
                    </HStack>
                  </Stack>
                  <IconButton
                    aria-label="remove"
                    onClick={removeFile}
                    variant="ghost"
                    icon={<FiTrash2 />}
                    size="sm"
                  />
                </HStack>
              )}
            </Stack>

            <HStack justify="flex-end" spacing={3}>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                isDisabled={!file}
                loadingText="Uploading..."
                isLoading={isUploading}
                type="submit"
              >
                Upload
              </Button>
            </HStack>
          </Stack>
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default UploadCSV;
